import React, { useCallback, useContext, useEffect, useState } from 'react';

import api from '../api';
import { Context as AuthContext } from '../../auth/context';
import { Context as CurrenciesContext } from '../../currencies/context';
import { ExchangeRate } from '../types';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import { getConvertRateByExchangeRate, isSameCurrency } from '../helpers';

import Context, { ExtendedContext } from './context';
import { GetConvertRate, GetRatesByCurrenciesQueryBody } from './BodyTypes';

const DEFAULT_CURRENCY_SYMBOL = '$';
interface Props {
    children: React.ReactElement;
}
const ExchangeRatesProvider = ({ children }: Props) => {
    const [data, setData] = useState<ExchangeRate[]>([]);
    const [todayData, setTodayData] = useState<ExchangeRate[]>([]);
    // All historical rates — prefetched on login for instant date-picker lookups (~27K rows, ~500KB)
    const [allRates, setAllRates] = useState<ExchangeRate[]>([]);
    const [error, setError] = useState({});
    const [listenToChangeInData, setListenToChangeInData] = useState(1);
    const addOneAlertChangeInData = () => {
        setListenToChangeInData((prev) => prev + 1);
    };
    const authContext = useContext(AuthContext);
    const currenciesContext = useContext(CurrenciesContext);

    useEffect(() => {
        if (authContext.currUser) {
            get({ date: Date.now() }).then((res) => {
                setTodayData(res);
            });
            // Prefetch all historical rates into memory
            api.getAll().then((res) => {
                if (Array.isArray(res)) setAllRates(res);
            }).catch(() => {}); // non-blocking
        }
    }, [authContext.currUser, listenToChangeInData]); // eslint-disable-line

    const getConvertRate = useCallback<GetConvertRate>(
        (base, quote, exchangeRateData) => {
            return getConvertRateByExchangeRate(base, quote, exchangeRateData || todayData);
        },
        [todayData]
    );

    /**
     * Get exchange rate for a currency pair on a specific date — from in-memory cache.
     * allRates is sorted by base, quote, createdAt DESC (from the server query).
     * Finds the most recent rate entry where createdAt <= the given date.
     * Returns { exchangeRateSell, exchangeRateBuy } or null.
     */
    const getRateForDate = useCallback(
        (base: any, quote: any, date: Date | number): { exchangeRateSell: number; exchangeRateBuy: number } | null => {
            const baseId = (typeof base === 'object' ? base?._id : base)?.toString();
            const quoteId = (typeof quote === 'object' ? quote?._id : quote)?.toString();
            if (!baseId || !quoteId || !date) return null;
            // A currency against itself is 1 — no row is ever stored for the identity pair.
            if (isSameCurrency(baseId, quoteId)) return { exchangeRateSell: 1, exchangeRateBuy: 1 };
            const targetMs = typeof date === 'number' ? date : date.getTime();
            // allRates is sorted by base, quote, createdAt DESC
            for (const rate of allRates) {
                if (rate.base._id?.toString() !== baseId || rate.quote._id?.toString() !== quoteId) continue;
                const rateMs = new Date(rate.createdAt).getTime();
                if (rateMs <= targetMs) {
                    return { exchangeRateSell: rate.exchangeRateSell, exchangeRateBuy: rate.exchangeRateBuy };
                }
            }
            return null;
        },
        [allRates]
    );

    const convertCurrency: ExtendedContext['convertCurrency'] = useCallback(
        (amount, base, quote, withoutSymbol, decimals = 2) => {
            const baseCurrencyId = typeof base !== 'object' ? base : base?._id;
            const stringAmount = taytanFormatNumberToString(amount);
            if (!baseCurrencyId) {
                return `${stringAmount}`;
            }

            const quoteCurrencyId = typeof quote !== 'object' ? quote : quote?._id;
            if (!quoteCurrencyId) {
                const baseSymbol = withoutSymbol
                    ? ''
                    : currenciesContext.dataAsArray.find((c) => c._id === baseCurrencyId)?.symbol || '';
                return `${stringAmount}${baseSymbol}`; // no quote
            }

            // Same currency in and out — the amount is already converted, at a rate of 1.
            if (isSameCurrency(baseCurrencyId, quoteCurrencyId)) {
                const symbol = withoutSymbol
                    ? ''
                    : currenciesContext.dataAsArray.find((c) => c._id === quoteCurrencyId)?.symbol ||
                      DEFAULT_CURRENCY_SYMBOL;
                return `${taytanFormatNumberToString(amount, decimals)}${symbol}`;
            }

            const exchangeRate = todayData.find(
                (rate) => rate.base._id === baseCurrencyId && rate.quote._id === quoteCurrencyId
            );
            if (!exchangeRate) {
                return `${stringAmount}`;
            }

            const convertedAmount = amount * exchangeRate.exchangeRateSell;

            const symbol = withoutSymbol ? '' : exchangeRate.quote?.symbol || DEFAULT_CURRENCY_SYMBOL;
            return `${taytanFormatNumberToString(convertedAmount, decimals)}${symbol}`;
        },
        [currenciesContext.dataAsArray, todayData]
    );

    const get = useCallback(
        (params) =>
            api
                .get(params)
                .then((res) => {
                    setData(res);
                    return res;
                })
                .catch(setError),
        []
    );

    const post = useCallback((body) => {
        const res = api
            .post(body)
            .then((response) => {
                addOneAlertChangeInData();
                return response;
            })
            .catch(setError);
        return res;
    }, []);

    const addMany = useCallback((body) => {
        const res = api
            .addMany(body)
            .then((response) => {
                addOneAlertChangeInData();
                return response;
            })
            .catch(setError);
        return res;
    }, []);

    const put = useCallback(async (body) => {
        try {
            const responseData = await api.put(body);
            addOneAlertChangeInData();
            return responseData;
        } catch (error) {
            return setError(error);
        }
    }, []);

    const getRatesByCurrencies = useCallback(async (query: GetRatesByCurrenciesQueryBody) => {
        try {
            const responseData = await api.getRatesByCurrencies(query);
            return responseData;
        } catch (error) {
            return setError(error);
        }
    }, []);

    return (
        <Context.Provider
            value={{
                get,
                post,
                put,
                data,
                setData,
                error,
                todayData,
                allRates,
                addMany,
                convertCurrency,
                getConvertRate,
                getRateForDate,
                getRatesByCurrencies
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default ExchangeRatesProvider;
